import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, errMsg, applyBrand } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const FIELDS = [["accent", "Accent", "Headings, labels, score ring, cover title"], ["tam", "TAM box", "Total Addressable Market"], ["sam", "SAM box", "Serviceable Available Market"], ["som", "SOM box", "Serviceable Obtainable Market"]];
const PRESETS = ["#059669", "#0EA5E9", "#1E3A8A", "#0F766E", "#B45309", "#BE123C", "#7C3AED", "#111827", "#CA8A04", "#15803D"];
const DEFAULTS = { accent: "#059669", tam: "#1E3A8A", sam: "#0F766E", som: "#B45309" };
const HEX = /^#[0-9A-Fa-f]{6}$/;

const ColorField = ({ k, label, hint, value, onChange }) => (
  <div className="rounded-lg border border-slate-800 bg-slate-950/40 p-4 space-y-3" data-testid={`brand-field-${k}`}>
    <div><p className="text-sm font-semibold">{label}</p><p className="text-xs text-slate-500">{hint}</p></div>
    <div className="flex items-center gap-2">
      <input type="color" data-testid={`brand-picker-${k}`} value={HEX.test(value) ? value : "#000000"} onChange={(e) => onChange(e.target.value.toUpperCase())} className="h-9 w-12 cursor-pointer rounded border border-slate-700 bg-transparent" />
      <Input data-testid={`brand-hex-${k}`} value={value} onChange={(e) => onChange(e.target.value.toUpperCase())} className={`num h-9 w-28 bg-slate-950/60 ${HEX.test(value) ? "border-slate-700" : "border-rose-500"}`} />
    </div>
    <div className="grid grid-cols-5 gap-1.5 w-fit">{PRESETS.map((p) => <button key={p} type="button" title={p} data-testid={`brand-preset-${k}-${p.slice(1)}`} onClick={() => onChange(p)} className={`h-5 w-5 rounded-full border ${value === p ? "ring-2 ring-white border-transparent" : "border-slate-700"}`} style={{ background: p }} />)}</div>
  </div>
);

const Preview = ({ b }) => (
  <div className="rounded-lg bg-white p-5 text-slate-900" data-testid="brand-preview">
    <p className="text-[10px] uppercase tracking-[0.3em] font-semibold text-center" style={{ color: b.accent }}>Feasibility Study</p>
    <p className="text-center font-display text-xl font-extrabold mb-4">Sample Project</p>
    <p className="text-sm font-bold mb-2" style={{ color: b.accent }}>02 Market Analysis</p>
    <div className="grid grid-cols-3 gap-2">{["tam", "sam", "som"].map((k) => <div key={k} className="rounded p-3 text-white" style={{ background: b[k] }}><p className="text-[10px] font-bold">{k.toUpperCase()}</p><p className="num font-bold">$1.2M</p></div>)}</div>
  </div>
);

export const BrandColors = ({ Card }) => {
  const { settings, setSettings } = useAuth();
  const [b, setB] = useState(null);
  useEffect(() => { if (settings?.brand && !b) setB({ ...settings.brand }); }, [settings]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!b) return null;
  const valid = Object.values(b).every((v) => HEX.test(v));
  const save = async () => {
    try {
      const r = await api.put("/settings", { company_name: settings.company_name, currency: settings.currency, tiers: settings.tiers, notify_email: settings.notify_email || null, brand: b });
      setSettings(r.data); applyBrand(r.data.brand); toast.success("Brand colors saved");
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Card title="Brand colors" testId="settings-brand-colors">
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">{FIELDS.map(([k, l, h]) => <ColorField key={k} k={k} label={l} hint={h} value={b[k]} onChange={(v) => setB({ ...b, [k]: v })} />)}</div>
        <div><p className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">Live preview</p><Preview b={b} /></div>
      </div>
      <p className="text-xs text-slate-500 mt-4">Used in the PDF, the Word export, the client portal and the staff study page.</p>
      <div className="flex gap-2 mt-6">
        <Button data-testid="brand-save-btn" disabled={!valid} onClick={save} className="rounded-full bg-emerald-500 text-emerald-950 hover:bg-emerald-400">Save brand colors</Button>
        <Button data-testid="brand-reset-btn" variant="ghost" onClick={() => setB({ ...DEFAULTS })}>Reset to defaults</Button>
      </div>
    </Card>
  );
};
